import VueClickAway from "vue3-click-away"
import router from './router'
import { createApp, defineAsyncComponent } from "vue"
import emitter from 'tiny-emitter/instance'
import { formatDateLong, formatDateShort, formatDateRelative } from './dateformats'

require('./bootstrap')

axios.interceptors.request.use(
    conf => {
        if (!conf.headers['Hide-Loading-Icon']) {
            EventBus.$emit('loading.start')
        }

        return conf
    },
    error => {
        EventBus.$emit('loading.stop')

        return Promise.reject(error)
    }
);
axios.interceptors.response.use(
    response => {
        EventBus.$emit('loading.stop')

        return response
    },
    error => {
        EventBus.$emit('loading.stop')

        // Handle Laravel Validation Messages
        if (error.response.status == 422) {
            let messages = error.response.data.errors
            for (var key in messages) {
                if (!messages.hasOwnProperty(key)) continue
                var obj = messages[key]
                for (var prop in obj) {
                    if(!obj.hasOwnProperty(prop)) continue
                    EventBus.$emit('alert.danger', obj[prop])
                }
            }
        }

        return Promise.reject(error)
    }
)

const setMetaTitle = (title) => {
    document.getElementsByTagName('title')[0].innerText = title
}

const app = createApp({
    el: '#app',
    data () {
        return {
            debug: false,
            loading: false,
            loaded: false,
            persist: {
                usersSearch: null,
            },
            navShowing: false,
        }
    },
    mounted () {
        if (this.debug) console.log('Vue mounted')

        this.$bus.$on('nav.toggle', function () {
            this.navShowing = !this.navShowing
        }.bind(this))

        this.$bus.$on('loading.start', function () {
            this.loading = true
        }.bind(this))

        this.$bus.$on('loading.stop', function () {
            this.loading = false
        }.bind(this))

        window.addEventListener('keyup', this.handleKeypress)

        window.Echo.private('control')
            .listen('.JournoLink\\Shared\\Events\\PressReleaseRequiresAuthorisation', (e) => {
                this.$bus.$emit('notifications.refresh')

                let audio = new Audio('/assets/sounds/unconvinced.ogg')
                audio.play()
            })

        const store = useStore()

        store.dispatch('populateTaxRates')
        store.dispatch('populateDefaultTaxRate')
        store.dispatch('populateSectors')
        store.dispatch('populateLocations')

        this.loaded = true
    },
    methods: {
        /**
         * @return {void}
         */
        goBack() {
            if (typeof this.$router.referer !== 'undefined') {
                this.$router.go(-1)
                return
            }

            this.$router.push({ name: 'home' });
        },
        onRouteLoad () {
            this.navShowing = false

            this.$nextTick(function () {
                var formFields = document.querySelectorAll('input.form-field')

                for (var i = 0; i < formFields.length; i++) {
                    let orig = formFields[i]

                    if (!orig.classList.contains('no-transform') && !orig.hasAttribute('data-transformed')) {
                        let wrapper = document.createElement('div')
                        wrapper.classList.add('form-field')

                        orig.setAttribute('data-transformed', true)

                        orig.parentNode.insertBefore(wrapper, orig)

                        let label = document.createElement('label')
                        label.innerText = orig.placeholder

                        wrapper.appendChild(orig)
                        wrapper.appendChild(label)
                    }
                }
            })
        },
        /**
         * Handle a global keypress event
         *
         * @param  {Object} event
         * @return {void}
         */
        handleKeypress(event) {
            // [Escape]
            if (event.keyCode === 27) {
                var instance = this.$route.matched[this.$route.matched.length - 1].instances['default']
                if (typeof instance.onEscapeKeypress == 'function') {
                    instance.onEscapeKeypress()
                }
            }
        },
    },
    watch:{
        $route (to, from) {
            this.onRouteLoad()
            this.$router.referer = from
        }
    },
});

const EventBus = {
    $on: (...args) => emitter.on(...args),
    $emit: (...args) => emitter.emit(...args),
}

app.config.globalProperties.$bus = EventBus // legacy this.$bus
export const useBus = () => app.config.globalProperties.$bus // modern useBus

app.config.globalProperties.$_ = _

import store from './store'
import Swal from "sweetalert2"
import {useStore} from "vuex"
import Wysiwyg from "./mixins/Wysiwyg"
import Permissions from "./mixins/Permissions"
import {currency} from "./mixins/Currency"

app.use(store);
app.use(VueClickAway)
app.use(router)

app.mixin(Wysiwyg)
app.mixin(Permissions)
app.mixin(currency)

app.provide('setMetaTitle', setMetaTitle)
app.config.globalProperties.setMetaTitle = setMetaTitle

app.provide('formatDateLong', formatDateLong)
app.config.globalProperties.formatDateLong = formatDateLong
app.provide('formatDateShort', formatDateShort)
app.config.globalProperties.formatDateShort = formatDateShort
app.provide('formatDateRelative', formatDateRelative)
app.config.globalProperties.formatDateRelative = formatDateRelative

app.provide('alert', Swal)
app.config.globalProperties.alert = Swal;

app.component('page-main', defineAsyncComponent(() => import('./components/Main')))
app.component('input-text', defineAsyncComponent(() => import('./components/InputText')))
app.component('loader', defineAsyncComponent(() => import('./components/Loader')))
app.component('field-error', defineAsyncComponent(() => import('./components/FieldError')))
app.component('close-icon', defineAsyncComponent(() => import('./components/CloseIcon')))
app.component('alert-notifications', defineAsyncComponent(() => import('./components/AlertNotifications')))
app.component('picker-selector', defineAsyncComponent(() => import('./components/PickerSelector')))
app.component('select-list', defineAsyncComponent(() => import('./components/SelectList')))
app.component('side-bar', defineAsyncComponent(() => import('./components/SideBar')))
app.component('keypress-handler', defineAsyncComponent(() => import('./components/KeyPressHandler')))
app.component('image-drop', defineAsyncComponent(() => import('./components/ImageDrop')))
app.component('multi-image-drop', defineAsyncComponent(() => import('./components/MultiImageDrop')))
app.component('notification-bar', defineAsyncComponent(() => import('./components/Notifications/NotificationBar')))
app.component('tab-modal', defineAsyncComponent(() => import('./components/TabModal')))
app.component('CheckBox', defineAsyncComponent(() => import('./components/CheckBox')))
app.component('simple-date-time-picker', defineAsyncComponent(() => import('./components/DateTimePicker/SimpleDateTimePicker')))

import vSelect from 'vue-select'
app.component('v-select', vSelect)

app.mount('#app')

export default app
